import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Building2 } from "lucide-react";

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: txns }] = await Promise.all([api.get("/transactions")]);
        // Show properties derived from transactions
        const seen = new Set();
        const list = [];
        for (const t of txns) {
          const key = t.property_data?.address;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          list.push({
            address: key,
            transaction_id: t.id,
            property_type: t.property_data?.property_type,
            listing_price: t.property_data?.listing_price,
            parcel: t.property_data?.parcel_number,
            county: t.property_data?.county,
            city: t.property_data?.city,
            state: t.property_data?.state,
            year_built: t.property_data?.year_built,
          });
        }
        setItems(list);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="properties-page">
      <div className="mb-6">
        <div className="kicker mb-2">Portfolio</div>
        <h1 className="text-4xl font-display">Properties</h1>
        <p className="text-sm text-slate-500 mt-2">Every property attached to a live transaction.</p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="grid-panel p-10 text-center">
          <Building2 size={28} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">Properties appear here once you add transactions.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Link
              key={p.address}
              to={`/transactions/${p.transaction_id}`}
              data-testid={`prop-card-${p.transaction_id}`}
              className="grid-panel overflow-hidden hover:border-brass transition-colors duration-150 group"
            >
              <div className="h-32 bg-navy-50 relative overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/4534504/pexels-photo-4534504.jpeg"
                  alt=""
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent"></div>
                <div className="absolute bottom-2 left-3 text-white text-[10px] uppercase tracking-widest">
                  {p.property_type || "Commercial"}
                </div>
              </div>
              <div className="p-4">
                <div className="text-sm font-medium truncate">{p.address}</div>
                <div className="mt-1 text-xs text-slate-500 font-mono">
                  {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Parcel</span>
                  <span className="font-mono">{p.parcel || "—"}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Year Built</span>
                  <span className="font-mono">{p.year_built || "—"}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
